/**
 * The particle sources a crash site puts up — AD-014c's fire and smoke.
 *
 * A sibling of `particle-sources.ts` rather than a fourth collector inside it:
 * that file sits at aislop's size ceiling, and this is a cohesive addition with
 * its own constants. It fills the same `BurningAircraft` slots the other three
 * collectors do, appended after them, and imports no `three`.
 *
 * **The toggle contract is AD-018's, unchanged.** ENHANCED EFFECTS' rule is
 * replace-not-add, and there is nothing here to replace: the original leaves no
 * fire on the ground where an aeroplane went in (`crash-sites.ts` has the
 * `pyro.c` evidence), so with the toggle off the picture is exactly what it was.
 * The *sim* tracks the sites either way — it must, or a rendering toggle would
 * move the encounter hash and with it every replay and every lockstep peer.
 *
 * **It does not double the wreck's own plume.** `encounter-views.ts` already
 * spawns an `explosion` sprite with `plume: true` on the `destroyed` event, and
 * that plume runs for `EFFECT_TUNABLES.plumeSeconds` = 6 s. This fire runs for
 * `CRASH_TUNABLES.flameSeconds` = 45 s from the same instant at the same place,
 * so the six seconds they share are the impact smoke handing over to the pyre,
 * which is the thing actually happening. No omit flag is needed and none is
 * added.
 */

import { crashFlameIntensity, crashSmokeIntensity, type CrashSite } from "@red-opus/engine";
import type { BurningAircraft, FireRunner } from "./particle-sources.ts";

/**
 * Metres above the impact point the flame band is anchored.
 *
 * The same two metres a burning building uses and for the identical reason:
 * `particle-effects.ts` throws its flame over `ENGINE_FORWARD_M` = 2 along the
 * source's own forward axis, and a source whose forward axis is straight *down*
 * turns that into a band sitting on the ground with the smoke — born two to
 * four metres "behind" the anchor — rising in a column above it.
 */
export const CRASH_FLAME_ANCHOR_M = 2;

/**
 * How hard a burnt-out aeroplane smokes, on `smokeIntensity`'s 0-1 scale.
 *
 * Above the 0.45 a burning cottage uses, because a wreck is a fuel fire rather
 * than a thatch fire and the black column is the whole point of it — and below
 * the 0.8 the scenery lane measured into a fog bank over the Somme, because six
 * of these can be alight at once and the same fog bank is available here.
 * Port-extension number, CALIBRATION-PENDING.
 */
export const CRASH_PLUME_INTENSITY = 0.6;

/**
 * How wide the fire is spread, as the `spreadScale` a building passes its own
 * half-extent in. A wrecked scout is a heap a few metres across rather than a
 * roof, so this is a small fixed number instead of anything read off the
 * airframe: the difference between a Camel's heap and a Gotha's is not worth a
 * second lookup table in the renderer.
 */
export const CRASH_SPREAD_M = 3.5;

/**
 * The empty list a runner without the field answers with.
 *
 * Module-level, and not the `?? []` its sibling spells: this runs every frame,
 * and an inline literal is a fresh array sixty times a second —
 * `scene-particles.test.ts` measures this path at *zero* bytes over 600 frames
 * and one empty array per frame is enough to fail it.
 */
const NO_CRASH_SITES: readonly CrashSite[] = [];

/**
 * Fills `into` from `offset` with every crash site still showing something, and
 * returns the new total — the same records `collectBurningAircraft`,
 * `collectTrainPlumes` and `collectSceneryFires` write, for the same
 * zero-allocation reason.
 *
 * A site that has finished smoking is skipped rather than dropped: the engine
 * keeps the record because the scar outlives the fire (`scene-crash-marks.ts`
 * draws that half), and this collector only asks whether there is anything in
 * the air above it.
 */
export function collectCrashFires(runner: FireRunner, into: BurningAircraft[], offset: number): number {
  let count = offset;
  // The fallback is the contract `collectSceneryFires` states: `FireRunner` is a
  // slice precisely so a test can hand these collectors a hand-made roster, and
  // several were written before this field existed.
  for (const site of runner.encounter().crashSites ?? NO_CRASH_SITES) {
    const smoke = crashSmokeIntensity(site);
    if (smoke <= 0) continue;
    const slot = into[count];
    if (!slot) break;
    slot.id = `crash-${site.id}`;
    slot.x = site.x;
    slot.y = site.y + CRASH_FLAME_ANCHOR_M;
    slot.z = site.z;
    // Forward is straight down and up is up: see `CRASH_FLAME_ANCHOR_M`.
    slot.forwardX = 0;
    slot.forwardY = -1;
    slot.forwardZ = 0;
    slot.upX = 0;
    slot.upY = 1;
    slot.upZ = 0;
    // Wreckage does not travel, so the column stands where it was made instead
    // of streaming off downwind.
    slot.vx = 0;
    slot.vy = 0;
    slot.vz = 0;
    slot.damageFraction = 1;
    slot.burning = crashFlameIntensity(site) > 0;
    // Nothing shoots a wreck in a way the spark path would know about — and
    // nothing should: it is already finished.
    slot.gunHitsTaken = 0;
    slot.plumeIntensity = CRASH_PLUME_INTENSITY * smoke;
    slot.spreadScale = CRASH_SPREAD_M;
    count += 1;
  }
  return count;
}
